import { useWishlist } from '../../../context/WishlistContext';
import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const RecentlyViewed = ({ products, onAddToCart }) => {
  const wishlist = useWishlist();
  const handleAddToCart = (product) => {
    if (onAddToCart) {
      onAddToCart(product);
    }
  };

  if (!products || products?.length === 0) {
    return null;
  }

  return (
    <div className="bg-background">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-heading font-semibold text-foreground">
          Recently Viewed
        </h2>
        <Button
          variant="ghost"
          size="sm"
          iconName="RotateCcw"
          iconPosition="left"
        >
          Clear History
        </Button>
      </div>
      <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide">
        {products?.map((product) => (
          <div key={product?.id} className="flex-shrink-0 w-64 bg-card border border-border rounded-lg overflow-hidden shadow-elevation-1 flex flex-col">
            {/* Product Image */}
            <div className="relative aspect-square bg-muted overflow-hidden">
              <Link to={`/product-details?id=${product?.id}`}>
                <Image
                  src={product?.image}
                  alt={product?.imageAlt}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                />
              </Link>
              
              {/* Quick Actions */}
              <div className="absolute top-3 right-3 flex flex-col gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); wishlist.toggle(product); }}
                  aria-label={wishlist.has(product?.id) ? 'Remove from wishlist' : 'Add to wishlist'}
                  className="bg-background/80 backdrop-blur-sm hover:bg-background"
                >
                  <Icon name="Heart" size={16} className={wishlist.has(product?.id) ? 'text-error fill-current' : ''} />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleAddToCart(product)}
                  className="bg-background/80 backdrop-blur-sm hover:bg-background"
                >
                  <Icon name="ShoppingCart" size={16} />
                </Button>
              </div>

              {/* Stock Status */}
              {!product?.inStock && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <span className="bg-error text-error-foreground text-sm font-medium px-3 py-1 rounded-md">
                    Out of Stock
                  </span>
                </div>
              )}
            </div>

            {/* Product Info */}
            <div className="p-4 flex flex-col flex-grow">
              <div className="mb-2">
                <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">
                  {product?.brand}
                </p>
                <Link to={`/product-details?id=${product?.id}`}>
                  <h3 className="text-sm font-medium text-foreground hover:text-primary transition-colors line-clamp-2 leading-tight">
                    {product?.name}
                  </h3>
                </Link>
              </div>

              {/* Rating */}
              <div className="flex items-center gap-1 mb-2">
                <div className="flex items-center">
                  {[...Array(5)]?.map((_, i) => (
                    <Icon
                      key={i}
                      name="Star"
                      size={12}
                      className={i < Math.floor(product?.rating) ? 'text-warning fill-current' : 'text-muted-foreground'}
                    />
                  ))}
                </div>
                <span className="text-xs text-muted-foreground">
                  {product?.rating}
                </span>
              </div>

              {/* Price */}
              <div className="flex items-center gap-2 mb-3">
                <span className="text-lg font-semibold text-foreground">
                  ${product?.price?.toFixed(2)}
                </span>
                {product?.originalPrice > 0 && (
                  <span className="text-sm text-muted-foreground line-through">
                    ${product?.originalPrice?.toFixed(2)}
                  </span>
                )}
              </div>

              {/* Last Viewed */}
              <p className="text-xs text-muted-foreground mb-auto">
                Viewed {product?.lastViewed}
              </p>

              {/* Add to Cart Button - Always at bottom */}
              <Button
                variant={product?.inStock ? "default" : "outline"}
                size="sm"
                fullWidth
                onClick={() => handleAddToCart(product)}
                disabled={!product?.inStock}
                iconName="ShoppingCart"
                iconPosition="left"
                className="mt-3"
              >
                {product?.inStock ? 'Add to Cart' : 'Out of Stock'}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RecentlyViewed;